
import { parkCategories } from '../../../mocks/parks';

interface CategoryFilterProps {
  activeCategory: string;
  onCategoryChange: (category: string) => void;
  viewMode: 'list' | 'map';
  onViewModeChange: (mode: 'list' | 'map') => void;
}

/**
 * Mapping of category names to Remix Icon class names.
 * Adding a fallback icon ensures the UI never breaks if a new
 * category is introduced without an explicit mapping.
 */
const categoryIcons: Record<string, string> = {
  All: 'ri-apps-line',
  Trails: 'ri-route-line',
  Playgrounds: 'ri-gamepad-line',
  'Sports Facilities': 'ri-trophy-line',
  'Dog Parks': 'ri-bear-smile-line',
  'Nature Preserves': 'ri-leaf-line',
  // fallback for any future categories
  default: 'ri-question-line',
};

export default function CategoryFilter({
  activeCategory,
  onCategoryChange,
  viewMode,
  onViewModeChange,
}: CategoryFilterProps) {
  // ---- Defensive programming ----
  if (!activeCategory) {
    console.warn('CategoryFilter: activeCategory is missing or empty.');
  }

  // Guard against a non‑function being passed for the callback.
  // We *never* reassign a prop – instead we create a safe wrapper.
  const safeOnCategoryChange =
    typeof onCategoryChange === 'function' ? onCategoryChange : () => {};

  // Guard for view‑mode callback as well (optional but defensive).
  const safeOnViewModeChange =
    typeof onViewModeChange === 'function' ? onViewModeChange : () => {};

  // Helper to fetch an icon class safely.
  const getIconClass = (category: string) =>
    categoryIcons[category] ?? categoryIcons.default;

  return (
    <section className="sticky top-20 z-40 bg-white border-b border-gray-100 shadow-sm">
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between gap-4">
          {/* Category buttons */}
          <div className="flex items-center gap-3 overflow-x-auto scrollbar-hide flex-1">
            {parkCategories.map((category) => (
              <button
                key={category}
                onClick={() => safeOnCategoryChange(category)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-medium text-sm transition-all whitespace-nowrap cursor-pointer ${
                  activeCategory === category
                    ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/25'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <i className={`${getIconClass(category)} text-base`}></i>
                {category}
              </button>
            ))}
          </div>

          {/* View Mode Toggle */}
          <div className="flex-shrink-0 flex items-center bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => safeOnViewModeChange('list')}
              className={`flex items-center gap-1.5 px-3.5 py-2 rounded-md text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                viewMode === 'list'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <i className="ri-list-unordered text-base"></i>
              List
            </button>
            <button
              onClick={() => safeOnViewModeChange('map')}
              className={`flex items-center gap-1.5 px-3.5 py-2 rounded-md text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                viewMode === 'map'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <i className="ri-map-2-line text-base"></i>
              Map
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
